import PageShell from "@/components/PageShell";
import useSEO, { orgSchema, breadcrumbSchema } from "@/lib/useSEO";
import { Link } from "react-router-dom";
import { MousePointer2, Upload, CreditCard, Sparkles, ArrowRight, ShieldCheck } from "lucide-react";

const STEPS = [
  {
    icon: MousePointer2,
    title: "Pick your block",
    body: "Open the homepage and drag on the 100×100 grid to select any empty area — from a single 10×10 block to a huge 40×40 mega billboard. The selection snaps to whole blocks so pricing stays clean.",
  },
  {
    icon: Upload,
    title: "Upload your logo",
    body: "Drop a PNG, JPG or SVG into the claim panel. We compose it at high resolution and stretch it across the exact rectangle you selected. Add your brand name, destination URL, 50-word description, and pick a category.",
  },
  {
    icon: CreditCard,
    title: "Pay $0.50 per pixel",
    body: "Stripe checkout accepts cards, Apple Pay, Google Pay and international payment methods. A single 10×10 block costs $50 (100 pixels × $0.50). The first 50 blocks of the launch are free — no card required.",
  },
  {
    icon: Sparkles,
    title: "You're live forever",
    body: "Within seconds of payment clearing your ad publishes on the grid and gets listed in our public /directory with a do-follow SEO backlink. Edit the logo, URL, description or category any time from your dashboard.",
  },
];

export default function HowItWorks() {
  useSEO({
    title: "How PixelEmpire Works — Claim Your Permanent Ad Block in 4 Steps",
    description:
      "Learn how to claim a permanent 10×10 advertising block on the PixelEmpire AI grid. Four simple steps: pick your block, upload your logo, pay $0.50/pixel via Stripe, and go live forever.",
    path: "/how-it-works",
    jsonLd: [
      orgSchema,
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "How it works", url: "/how-it-works" },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: "How to claim a permanent PixelEmpire advertising block",
        totalTime: "PT3M",
        step: STEPS.map((s, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          name: s.title,
          text: s.body,
        })),
      },
    ],
  });

  return (
    <PageShell testId="page-how-it-works">
      <div className="pill" data-testid="hiw-pill"><span>How it works</span></div>
      <h1 className="mt-4 font-display text-4xl sm:text-5xl leading-tight text-white" data-testid="hiw-h1">
        How to claim your <span className="text-gradient">permanent</span> ad block
      </h1>
      <p className="mt-4 text-base sm:text-lg text-[var(--fg-muted)] max-w-3xl">
        From landing page to lifetime placement in under three minutes. No accounts required
        for the free tier; no monthly bills, ever.
      </p>

      <ol className="mt-10 space-y-6" data-testid="hiw-steps">
        {STEPS.map(({ icon: Icon, title, body }, i) => (
          <li key={title} className="glass p-6 sm:p-7 rounded-2xl" data-testid={`hiw-step-${i + 1}`}>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-400 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="font-display text-xl text-white leading-tight">
                  <span className="text-[var(--fg-muted)] font-mono text-sm mr-2">Step {i + 1}</span>
                  {title}
                </h2>
                <p className="mt-2 text-sm sm:text-base text-[var(--fg-muted)] leading-relaxed">{body}</p>
              </div>
            </div>
          </li>
        ))}
      </ol>

      <section className="mt-14 glass p-6 rounded-2xl">
        <h2 className="font-display text-2xl text-white flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-emerald-300" /> What you get with every block
        </h2>
        <ul className="mt-4 space-y-2 text-sm text-[var(--fg-muted)] list-disc list-inside">
          <li>Permanent placement on the homepage 100×100 grid</li>
          <li>Do-follow SEO backlink from the public <Link to="/directory" className="text-cyan-300 underline">/directory</Link></li>
          <li>Category filtering + brand search discoverability</li>
          <li>Click and hover analytics per block</li>
          <li>Editable dashboard for logo / URL / description / category</li>
          <li>Downloadable "Empire Plot" share card for X / Reddit</li>
        </ul>
      </section>

      <div className="mt-12 flex flex-wrap items-center gap-3">
        <Link to="/" className="btn-primary" data-testid="hiw-cta-primary">
          Claim your block <ArrowRight className="w-4 h-4" />
        </Link>
        <Link to="/pricing" className="btn-ghost" data-testid="hiw-cta-pricing">See pricing</Link>
        <Link to="/faq" className="btn-ghost">Read the FAQ</Link>
      </div>
    </PageShell>
  );
}
